import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { useWindowWidth } from '@elfsight/lib-utils';

interface PopupStyles {
  outerMargin: number;
  innerPaddingHorizontal: number;
  innerPaddingVertical: number;
  fullScreen: boolean;
  borderRadius: number;
  headerFontStyle: TextStyleValue;
  captionFontStyle: TextStyleValue;
  errorColor: string;
}

interface EmailSubmitPopupControlContextProps {
  isFormPopupOpen: boolean;
  openFormPopup: () => void;
  closeFormPopup: () => void;
  popupStyles: PopupStyles;
}

interface EmailSubmitPopupProviderProps {
  children: ReactNode;
}

const BASE_POPUP_STYLES = {
  borderRadius: 20,
  outerMargin: 40,
  errorColor: 'rgb(255, 56, 56)',
  innerPaddingHorizontal: 60,
  innerPaddingVertical: 60,
  headerFontStyle: {
    fontSize: 24,
    fontWeight: 'bold' as const,
    lineHeight: '30px'
  },
  captionFontStyle: {
    fontSize: 15,
    fontWeight: 'normal' as const,
    lineHeight: '20px'
  },
  fullScreen: false
};

const POPUP_STYLES = {
  style1: {
    ...BASE_POPUP_STYLES
  },
  style2: {
    ...BASE_POPUP_STYLES,
    innerPaddingHorizontal: 40,
    innerPaddingVertical: 40
  },
  style3: {
    ...BASE_POPUP_STYLES,
    outerMargin: 0,
    innerPaddingHorizontal: 20,
    innerPaddingVertical: 48,
    fullScreen: true,
    borderRadius: 0,
    headerFontStyle: {
      fontSize: 22,
      fontWeight: 'bold' as const,
      lineHeight: '28px'
    }
  }
};

const EmailSubmitPopupControlContext = createContext<
  EmailSubmitPopupControlContextProps | undefined
>(undefined);

export const EmailSubmitPopupProvider = ({
  children
}: EmailSubmitPopupProviderProps) => {
  const [isFormPopupOpen, setIsFormPopupOpen] = useState(false);
  const windowWidth = useWindowWidth();

  const openFormPopup = useCallback(() => setIsFormPopupOpen(true), []);
  const closeFormPopup = useCallback(() => setIsFormPopupOpen(false), []);

  const currentStyle = useMemo(() => {
    if (windowWidth >= 580) {
      return 'style1';
    }
    if (windowWidth >= 460) {
      return 'style2';
    }
    return 'style3';
  }, [windowWidth]);

  const popupStyles = POPUP_STYLES[currentStyle];

  return (
    <EmailSubmitPopupControlContext.Provider
      value={{ isFormPopupOpen, openFormPopup, closeFormPopup, popupStyles }}
    >
      {children}
    </EmailSubmitPopupControlContext.Provider>
  );
};

export const useEmailSubmitPopup = (): EmailSubmitPopupControlContextProps => {
  const context = useContext(EmailSubmitPopupControlContext);
  if (!context) {
    throw new Error(
      'useEmailSubmitPopup must be used within a EmailSubmitPopupProvider'
    );
  }
  return context;
};
